class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.selectedId = null;
    this.userLoc = null;
  }

  connectedCallback() {
    this.list = this.querySelector('[data-sl-list]');
    this.cards = Array.from(this.querySelectorAll('[data-sl-card]'));
    this.empty = this.querySelector('[data-sl-empty]');
    this.searchInput = this.querySelector('[data-sl-search]');
    this.stateSelect = this.querySelector('[data-sl-state]');
    this.locateBtn = this.querySelector('[data-sl-locate]');
    this.locateLabel = this.querySelector('[data-sl-locate-label]');
    this.countEl = this.querySelector('[data-sl-count]');
    this.regionEl = this.querySelector('[data-sl-region]');
    this.mapIframe = this.querySelector('[data-sl-map-iframe]');
    this.viewingName = this.querySelector('[data-sl-viewing-name]');
    this.viewingAddress = this.querySelector('[data-sl-viewing-address]');
    this.callLink = this.querySelector('[data-sl-call]');
    this.directionsLink = this.querySelector('[data-sl-directions]');

    this.stores = this.cards.map((el) => ({
      el,
      id: el.dataset.slId,
      name: el.dataset.slName,
      state: el.dataset.slState,
      address: el.dataset.slAddress,
      phone: el.dataset.slPhone,
      email: el.dataset.slEmail,
      mapLink: el.dataset.slMapLink,
      lat: parseFloat(el.dataset.slLat),
      lng: parseFloat(el.dataset.slLng),
    }));

    this.selectedId = this.stores.length ? this.stores[0].id : null;

    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        this.selectedId = card.dataset.slId;
        this.render();
      });
      card.addEventListener('keydown', (e) => {
        if (e.target.closest('a')) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectedId = card.dataset.slId;
          this.render();
        }
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.render());
    }
    if (this.stateSelect) {
      this.stateSelect.addEventListener('change', () => this.render());
    }
    if (this.locateBtn) {
      this.locateBtn.addEventListener('click', () => this.useLocation());
    }

    // ---- state tabs (added): only filters the nav-track strip, nothing else ----
    this.navItems = Array.from(this.querySelectorAll('[data-sl-nav-item]'));
    this.stateTabs = Array.from(this.querySelectorAll('[data-sl-state-tab]'));
    this.activeState = 'All';

    this.navItems.forEach((item) => {
      item.addEventListener('click', () => {
        const id = item.dataset.slNavTarget;
        const store = this.stores.find((s) => s.id === id);
        if (!store) return;
        this.selectSilently(store);
        store.el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    this.stateTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activeState = tab.dataset.slStateValue;
        this.stateTabs.forEach((t) => t.classList.toggle('is-active', t === tab));
        this.filterNavTrack();
      });
    });

    this.render();
  }

  dist(a, b) {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((a.lat * Math.PI) / 180) *
        Math.cos((b.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  useLocation() {
    if (!navigator.geolocation) {
      if (this.locateLabel) this.locateLabel.textContent = 'Location unavailable — check permissions';
      return;
    }
    if (this.locateLabel) this.locateLabel.textContent = 'Locating…';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.selectedId = null;
        if (this.locateLabel) this.locateLabel.textContent = 'Sorted by distance from you';
        this.render();
      },
      () => {
        if (this.locateLabel) this.locateLabel.textContent = 'Location unavailable — check permissions';
      },
      { timeout: 8000 }
    );
  }

  telHref(phone) {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('91') && digits.length > 10) return 'tel:+' + digits;
    return 'tel:+91' + digits.replace(/^0+/, '');
  }

  directionsHref(store) {
    if (store.mapLink) return store.mapLink;
    return 'https://www.google.com/maps/search/' + encodeURIComponent(store.address);
  }

  mapSrc(store) {
    return 'https://maps.google.com/maps?q=' + encodeURIComponent(store.address) + '&z=15&output=embed';
  }

  updateMap(store) {
    if (!this.mapIframe || this.currentMapId === store.id) return;
    this.currentMapId = store.id;
    const fresh = this.mapIframe.cloneNode(false);
    fresh.src = this.mapSrc(store);
    this.mapIframe.replaceWith(fresh);
    this.mapIframe = fresh;
  }

  // ---- (added) only toggles nav-track item visibility, doesn't touch cards/list/search ----
  filterNavTrack() {
    this.navItems.forEach((item) => {
      const matchesState = this.activeState === 'All' || item.dataset.slNavState === this.activeState;
      item.hidden = !matchesState;
    });
  }

  // ---- (added) lightweight update used only by nav-item click: no list reorder/filter ----
  selectSilently(store) {
    this.selectedId = store.id;

    this.stores.forEach((s) => {
      s.el.classList.toggle('is-selected', s.id === store.id);
    });

    this.navItems.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.slNavTarget === store.id);
    });

    this.updateMap(store);
    if (this.viewingName) this.viewingName.textContent = store.name;
    if (this.viewingAddress) {
      this.viewingAddress.textContent = store.address.split(',').slice(-3).join(',').trim();
    }
    if (this.callLink) {
      const href = this.telHref(store.phone);
      this.callLink.hidden = !href;
      if (href) this.callLink.href = href;
    }
    if (this.directionsLink) this.directionsLink.href = this.directionsHref(store);
  }

  render() {
    const query = (this.searchInput?.value || '').toLowerCase();
    const stateFilter = this.stateSelect?.value || 'All';

    let filtered = this.stores.filter((s) => {
      const haystack = (s.name + ' ' + s.address + ' ' + s.state + ' ' + s.phone).toLowerCase();
      return haystack.includes(query) && (stateFilter === 'All' || s.state === stateFilter);
    });

    if (this.userLoc) {
      filtered = filtered.slice().sort((a, b) => this.dist(this.userLoc, a) - this.dist(this.userLoc, b));
    }

    const visibleIds = new Set(filtered.map((s) => s.id));
    let selected = filtered.find((s) => s.id === this.selectedId) || filtered[0] || null;
    this.selectedId = selected ? selected.id : null;

    filtered.forEach((store) => {
      const areaEl = store.el.querySelector('[data-sl-card-area]');
      if (areaEl) {
        areaEl.textContent = this.userLoc
          ? store.state + ' · ' + this.dist(this.userLoc, store).toFixed(1) + ' km away'
          : store.state;
      }
      this.list.appendChild(store.el);
    });

    this.stores.forEach((store) => {
      store.el.hidden = !visibleIds.has(store.id);
      store.el.classList.toggle('is-selected', !!(selected && store.id === selected.id));
    });

    if (this.empty) this.empty.hidden = filtered.length !== 0;

    if (this.countEl) {
      this.countEl.textContent = filtered.length + (filtered.length === 1 ? ' Store' : ' Stores');
    }
    if (this.regionEl) {
      const states = [...new Set(this.stores.map((s) => s.state))];
      this.regionEl.textContent = (stateFilter === 'All' ? states.join(' · ') : stateFilter) + ', India';
    }

    // ---- (added) keep nav-track active state in sync when a card is clicked directly ----
    if (this.navItems && this.navItems.length) {
      this.navItems.forEach((item) => {
        item.classList.toggle('is-active', !!(selected && item.dataset.slNavTarget === selected.id));
      });
    }

    if (selected) {
      this.updateMap(selected);
      if (this.viewingName) this.viewingName.textContent = selected.name;
      if (this.viewingAddress) {
        this.viewingAddress.textContent = selected.address.split(',').slice(-3).join(',').trim();
      }
      if (this.callLink) {
        const href = this.telHref(selected.phone);
        this.callLink.hidden = !href;
        if (href) this.callLink.href = href;
      }
      if (this.directionsLink) this.directionsLink.href = this.directionsHref(selected);
    }
  }
}

customElements.define('store-locator', StoreLocator);